<template>
  <div>
    <div class="container">
      <h3 class="page-title">Connected Accounts</h3>

      <div class="d-flex align-item-end justify-end px-3 mb-2">
        <v-btn
          variant="outlined"
          :loading="loading"
          @click="onConnect"
          prepend-icon="mdi-plus"
        >
          Connect
        </v-btn>
      </div>

      <div v-if="!stateUser?.githubAccounts.length">
        <v-alert
          type="info"
          variant="tonal"
          text="No GitHub accounts are connected."
          border="start"
          class="mb-4"
        />
      </div>
      <v-list v-else lines="one" class="rounded-lg">
        <v-list-item
          v-for="githubAccount in stateUser?.githubAccounts"
          :key="githubAccount.login"
        >
          <!-- Avatar -->
          <template #prepend>
            <v-avatar size="32" class="mr-2">
              <v-img
                :src="githubAccount.avatar"
                alt="GitHub Avatar"
              />
            </v-avatar>
          </template>

          <!-- Username -->
          <v-list-item-title>GitHub</v-list-item-title>
          <v-list-item-subtitle>
            <span>@{{ githubAccount.login }}</span>
          </v-list-item-subtitle>

          <!-- Actions -->
          <template #append>
            <div class="d-flex align-center ga-2">
              <v-btn
                size="small"
                color="error"
                variant="tonal"
                @click="() => onDisconnect(githubAccount.login)"
                prepend-icon="mdi-close"
                :loading="loading"
              >
                Disconnect
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import gitHubApi from '@/api/gitHubApi'

const store = useStore()
const stateUser = computed(() => store.state.user.user)

const loading = ref(false)

const onConnect = () => {
  const clientId = process.env.VUE_APP_GITHUB_CLIENT_ID
  const currentOrigin = window.location.origin
  const redirectUri = encodeURIComponent(`${currentOrigin}/github/callback`)
  const scope = 'repo'
  const authUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}`

  window.open(authUrl, '_blank', 'width=600,height=700')

  window.addEventListener('message', async (event) => {
    if (event.origin !== currentOrigin) return // adjust to your domain
    if (event.data.type === 'github-auth') {
      const authCode = event.data.code

      const response = await gitHubApi.saveGitHubToken(authCode)

      if (response.success) {
        // console.log('Got GitHub auth result:', response.resData)

        await store.dispatch('user/updateUser', { ...stateUser.value, githubAccounts: response.resData.githubAccounts })
      } else {
        store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    }
  })
}

const onDisconnect = async (username) => {
  const response = await gitHubApi.removeGitHubToken(username)

  if (response.success) {
    // console.log('Got GitHub auth result:', response.resData)

    await store.dispatch('user/updateUser', { ...stateUser.value, githubAccounts: response.resData.githubAccounts })
  } else {
    store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.v-btn:not(.v-btn--icon) {
  border-radius: 4px !important;
}
</style>
